import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { RBarChart } from '../../../../ChartUtility/recharts';
import MeterChart from '../details/MeterChart';
import RefreshControl from '../RefreshControl';
import PeriodComparison from './PeriodComparison';
import ComparisonSummary from './ComparisonSummary';
import { CompareBy, Field, GroupPair, Picker, G1_COLOR, G2_COLOR } from './ComparisonControls';
import { useComparisonSeries } from './useComparisonSeries';
import { kpiCatalogue, meterOptions, kpiOfMeter } from '../../utils/meterKpis';
import { overlayRows, mergeBreakdowns, changeBetween, withTrend } from '../../utils/comparisonSeries';
import { toCsv } from '../../utils/waterExport';
import { downloadCsv } from '../../utils/downloadCsv';
import '../../styles/WaterComparison.css';

/**
 * Two groups of the same meter figure, against each other.
 *
 * Built to the shape Carbon Footprinting established on Scope 1, because a
 * reader who has learnt that page should not have to learn this one: the
 * dimension being compared splits into Group 1 and Group 2, every other
 * dimension is held still and badged COMMON, and the answer arrives as two
 * totals, the gap between them, a trend and a breakdown.
 *
 * What is being measured differs. On Scope 1 it is fixed — a scope has one
 * number — so the page asks only for a year and a place. A fleet has one
 * figure per meter, so the meter is the third control: a group selector when
 * two meters are being compared, a COMMON filter when they are not.
 *
 * The meter, not its KPI, is what the reader names. Several meters can share a
 * KPI, so choosing the KPI silently read their sum; and on the review call the
 * KPI was the one idea that had to be explained, while nobody has ever had to
 * be told what "32 - SPORTS CLUB" means.
 *
 * Every group resolves its own window from its own financial year. That is not
 * a detail: a window anchored to today matches nothing in a year that does not
 * contain today, which is exactly the fault the dashboard's year filter had.
 */

const FY = 'fy';
const METER = 'meter';
const PERIOD = 'period';

/** Name the fitted trend series carries, so the chart draws it flat. */
const TREND = 'Trend';

const BARS = 'bars';
const LINE = 'line';

/** Ranges offered, less the custom one — two groups would need two of them. */
/** Columns of the exported comparison. */
const EXPORT_COLUMNS = ['Section', 'Group', 'Item', 'Value', 'Unit'];

/**
 * Room one meter's bar needs, laid on its side.
 *
 * Names like "11 - Canteen - Incoming meter (Main inlet to car)" do not fit
 * under a column. Angled at twenty degrees they overlapped one another and ran
 * off the panel; along the axis their length costs nothing.
 */
const ROW_HEIGHT = 44;

/**
 * Names an option.
 * @param {Array<object>} options - The options to look in.
 * @param {*} value - The selected value.
 * @param {string} fallback - Shown when nothing matches.
 * @returns {string} The label.
 */
const labelOf = (options, value, fallback = '') =>
  options.find((option) => String(option.value) === String(value))?.label || fallback;

/**
 * The tab's own chrome.
 *
 * It carries `im-root` for the same reason the dashboard does: the stylesheet
 * is scoped under that class, and leaning on whichever page mounted it left
 * the other route unstyled.
 *
 * @param {Function} onRefresh - Asks the page's owner to refetch.
 * @param {React.ReactNode} children - The comparison.
 */
const Shell = ({ onRefresh, children }) => (
  <div className="im-root wc-root">
    <div className="wc-header">
      <h2>Meter Comparison</h2>
      <RefreshControl onRefresh={onRefresh} />
    </div>
    {children}
  </div>
);

/**
 * @param {Array<object>} meters - Meters, for the KPIs they are mapped to.
 * @param {Array<object>} financialYearOptions - Years the company reports on.
 * @param {Array<object>} sourceOptions - Locations, each with its sub-locations.
 * @param {Function} onRefresh - Asks the page's owner to refetch.
 */
const WaterComparisonView = ({
  meters = [],
  financialYearOptions = [],
  sourceOptions = [],
  onRefresh,
}) => {
  const catalogue = useMemo(() => kpiCatalogue(meters), [meters]);
  const meterSelectOptions = useMemo(() => meterOptions(meters), [meters]);

  const [compareByRaw, setCompareBy] = useState(null);
  const [trendChart, setTrendChart] = useState(BARS);

  const [showTrendLine, setShowTrendLine] = useState(false);

  const [commonFy, setCommonFy] = useState(financialYearOptions?.[0]?.value ?? '');
  const [commonLocation, setCommonLocation] = useState('');
  const [commonMeter, setCommonMeter] = useState(meterSelectOptions?.[0]?.value ?? '');

  /* The tab opens on an answer rather than on an empty form: the two most
     recent years are what a reader almost always wants, and having to fill in
     three dropdowns before anything appears is the cost Scope 1 avoids. */
  const [g1Fy, setG1Fy] = useState(financialYearOptions?.[0]?.value ?? '');
  const [g2Fy, setG2Fy] = useState(financialYearOptions?.[1]?.value ?? '');
  const [g1Meter, setG1Meter] = useState(meterSelectOptions?.[0]?.value ?? '');
  const [g2Meter, setG2Meter] = useState(meterSelectOptions?.[1]?.value ?? '');

  /**
   * A dimension is only offered when the data holds two of it, so a company
   * reporting on one year is never invited to compare it against itself, and a
   * fleet of one meter is not offered a meter comparison.
   */
  const dimensions = useMemo(
    () =>
      [
        financialYearOptions.length >= 2 && { key: FY, label: 'Financial Year' },
        meterSelectOptions.length >= 2 && { key: METER, label: 'Meter' },
        { key: PERIOD, label: 'Period' },
      ].filter(Boolean),
    [financialYearOptions, meterSelectOptions]
  );

  const compareBy = dimensions.some((dimension) => dimension.key === compareByRaw)
    ? compareByRaw
    : dimensions[0]?.key;

  const picked = {
    [FY]: [g1Fy, g2Fy],
    [METER]: [g1Meter, g2Meter],
  }[compareBy] || ['', ''];

  /**
   * Comparing two years still needs to know which meter, so a group is only
   * ready once the one held still has been named too.
   */
  const ready =
    Boolean(picked[0]) &&
    Boolean(picked[1]) &&
    String(picked[0]) !== String(picked[1]) &&
    (compareBy === METER || Boolean(commonMeter));

  /**
   * One group's scope.
   *
   * Every comparison reads a whole financial year in monthly buckets. That is
   * the window the question is asked over — "how does this year compare", "is
   * the inlet tracking the outlet" — so a range and a bucket size were two
   * decisions that never changed the answer, only how it was drawn.
   *
   * The KPI comes from the meter rather than being chosen: a meter is mapped
   * to exactly one, so naming the meter names the KPI. That is what lets an
   * inlet be compared against an outlet even though they sit in different
   * categories.
   *
   * @param {number} index - 0 for Group 1, 1 for Group 2.
   * @returns {object} Scope for `useComparisonSeries`.
   */
  const groupScope = (index) => {
    const financialYear = compareBy === FY ? (index === 0 ? g1Fy : g2Fy) : commonFy;
    const meterId = compareBy === METER ? (index === 0 ? g1Meter : g2Meter) : commonMeter;

    return {
      kpi: kpiOfMeter(meters, meterId, financialYear),
      meterIds: meterId ? [meterId] : undefined,
      financialYear,
      locationId: commonLocation,
      timeRange: 'thisFY',
      aggregation: 'month',
      enabled: ready,
    };
  };

  const group1 = useComparisonSeries(groupScope(0));
  const group2 = useComparisonSeries(groupScope(1));

  const names = useMemo(() => {
    if (compareBy === FY) {
      return [
        `FY ${labelOf(financialYearOptions, g1Fy)}`,
        `FY ${labelOf(financialYearOptions, g2Fy)}`,
      ];
    }
    return [
      labelOf(meterSelectOptions, g1Meter, 'Group 1'),
      labelOf(meterSelectOptions, g2Meter, 'Group 2'),
    ];
  }, [compareBy, financialYearOptions, meterSelectOptions, g1Fy, g2Fy, g1Meter, g2Meter]);

  /* Two financial years read their own dates, so the rows are laid out by
     month of the year rather than by timestamp: April sits above April and the
     axis reads Apr to Mar, instead of running across two calendar years. */
  const plotted = useMemo(
    () =>
      overlayRows(
        group1.rows,
        group2.rows,
        names[0],
        names[1],
        compareBy === FY ? { a: g1Fy, b: g2Fy } : null
      ),
    [group1.rows, group2.rows, names, compareBy, g1Fy, g2Fy]
  );

  const trendRows = useMemo(
    () => (showTrendLine ? withTrend(plotted, TREND) : plotted),
    [plotted, showTrendLine]
  );

  const breakdown = useMemo(
    () => mergeBreakdowns(group1.breakdown, group2.breakdown),
    [group1.breakdown, group2.breakdown]
  );

  /**
   * Which meter drove the difference is only a question when both groups hold
   * the same meters — two years of one fleet, or two periods of it.
   *
   * Comparing one meter against another, each belongs to a single group, so
   * every row carried one real figure and a zero: two bars on a diagonal, a
   * tooltip quoting nought for whichever meter the row was not about, and the
   * two totals overhead already saying it. The panel is left out rather than
   * drawn empty.
   */
  const showBreakdown = breakdown.length > 0 && compareBy !== METER;

  const change = changeBetween(group1.total, group2.total);
  const loading = group1.isLoading || group2.isLoading;
  const failed = group1.error || group2.error;

  /** Two meters can measure in different units, and a difference across them
      would be arithmetic on unlike things. */
  const mixedUnits = compareBy === METER && group1.unit !== group2.unit;
  const unit = group1.unit;

  const heading =
    compareBy === METER
      ? `${names[0]} Vs ${names[1]}`
      : labelOf(meterSelectOptions, commonMeter, 'Total');

  const noun = { [FY]: 'financial years', [METER]: 'meters' }[compareBy] || 'groups';

  const handleExport = () => {
    const rows = [
      EXPORT_COLUMNS,
      ['Total', names[0], heading, group1.total, group1.unit],
      ['Total', names[1], heading, group2.total, group2.unit],
      ...breakdown.flatMap((part) => [
        ['By meter', names[0], part.category, part.g1, group1.unit],
        ['By meter', names[1], part.category, part.g2, group2.unit],
      ]),
    ];

    downloadCsv(`meter-comparison-${compareBy}.csv`, toCsv(rows));
  };

  const controls = (
    <CompareBy dimensions={dimensions} value={compareBy} onChange={setCompareBy} />
  );

  if (compareBy === PERIOD) {
    return (
      <Shell onRefresh={onRefresh}>
        <PeriodComparison
          controls={controls}
          meters={meters}
          financialYearOptions={financialYearOptions}
          sourceOptions={sourceOptions}
        />
      </Shell>
    );
  }

  return (
    <Shell onRefresh={onRefresh}>
      <section className="wc-panel">
        <div className="wc-panel-head">
          <div>
            <h3 className="wc-panel-title">Filters</h3>
            <span className="wc-panel-sub">Compare two groups against each other</span>
          </div>
          {controls}
        </div>

        {catalogue.length === 0 ? (
          <p className="wc-empty">
            No meter is mapped yet, so there is nothing to compare.
          </p>
        ) : (
          <div className="wc-filters">
            {compareBy === FY ? (
              <GroupPair
                label="Financial Year"
                id="wc-fy"
                g1={g1Fy}
                g2={g2Fy}
                onG1={setG1Fy}
                onG2={setG2Fy}
                options={financialYearOptions}
                placeholder="Select FY…"
              />
            ) : (
              <Field label="Financial Year" htmlFor="wc-common-fy" common>
                <Picker
                  id="wc-common-fy"
                  value={commonFy}
                  onChange={setCommonFy}
                  options={financialYearOptions}
                  placeholder="Select FY…"
                />
              </Field>
            )}

            {compareBy === METER ? (
              <GroupPair
                label="Meter"
                id="wc-meter"
                g1={g1Meter}
                g2={g2Meter}
                onG1={setG1Meter}
                onG2={setG2Meter}
                options={meterSelectOptions}
                placeholder="Select meter…"
              />
            ) : (
              <Field label="Meter" htmlFor="wc-common-meter" common>
                <Picker
                  id="wc-common-meter"
                  value={commonMeter}
                  onChange={setCommonMeter}
                  options={meterSelectOptions}
                  placeholder="Select meter…"
                />
              </Field>
            )}

            <Field label="Location" htmlFor="wc-common-loc" common>
              <Picker
                id="wc-common-loc"
                value={commonLocation}
                onChange={setCommonLocation}
                options={sourceOptions}
                placeholder="All Locations"
              />
            </Field>
          </div>
        )}
      </section>

      {catalogue.length === 0 ? null : !ready ? (
        <section className="wc-panel wc-empty">
          {compareBy !== METER && !commonMeter
            ? `Pick a meter, then two different ${noun} (Group 1 and Group 2).`
            : `Pick two different ${noun} (Group 1 and Group 2) to see the comparison.`}
        </section>
      ) : failed ? (
        <section className="wc-panel wc-empty wc-empty--bad" role="alert">
          One of the two requests failed, so there is nothing here to compare.
        </section>
      ) : loading ? (
        <div className="wc-skeleton" aria-label="Loading comparison" />
      ) : (
        <>
          <section className="wc-panel">
            <div className="wc-panel-head">
              <div>
                <h3 className="wc-panel-title">{heading}</h3>
                <span className="wc-panel-sub">
                  {unit} · Group 1 Vs Group 2
                </span>
              </div>
              <button type="button" className="wd-btn wd-btn--primary" onClick={handleExport}>
                <FontAwesomeIcon icon={faDownload} />
                Export CSV
              </button>
            </div>

            <ComparisonSummary
              g1={{ name: names[0], total: group1.total }}
              g2={{ name: names[1], total: group2.total }}
              change={change}
              unit={unit}
              mixedUnits={mixedUnits}
              units={[group1.unit, group2.unit]}
            />
          </section>

          <section className="wc-panel">
            <div className="wc-panel-head">
              <div>
                <h3 className="wc-panel-title">Trend</h3>
                <span className="wc-panel-sub">
                  Monthly, {unit}
                  {compareBy === FY && ' · April to March, both years'}
                </span>
              </div>
              <div className="wc-toggle" role="tablist" aria-label="Trend chart type">
                {[[BARS, 'Bars'], [LINE, 'Line']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={trendChart === value}
                    className={`wc-toggle-btn${trendChart === value ? ' wc-toggle-btn--on' : ''}`}
                    onClick={() => setTrendChart(value)}
                  >
                    {label}
                  </button>
                ))}

                <button
                  type="button"
                  className={`wc-toggle-btn${showTrendLine ? ' wc-toggle-btn--on' : ''}`}
                  aria-pressed={showTrendLine}
                  onClick={() => setShowTrendLine((on) => !on)}
                >
                  Trend line
                </button>
              </div>
            </div>

            <MeterChart
              rows={trendRows}
              graphType={trendChart === BARS ? 'periodTotal' : 'seriesComparison'}
              stackByField="kpi"
              trendName={showTrendLine ? TREND : null}
              unit={unit}
              aggregation="month"
              height={330}
            />
          </section>

          {showBreakdown && (
            <section className="wc-panel">
              <div className="wc-panel-head">
                <div>
                  <h3 className="wc-panel-title">By meter</h3>
                  <span className="wc-panel-sub">
                    {unit} over the window · which meter drove the difference
                  </span>
                </div>
              </div>

              <RBarChart
                data={breakdown}
                xKey="category"
                series={[
                  { key: 'g1', name: names[0], color: G1_COLOR },
                  { key: 'g2', name: names[1], color: G2_COLOR },
                ]}
                unit={unit}
                height={Math.max(220, breakdown.length * ROW_HEIGHT)}
                horizontal
                animate
              />
            </section>
          )}
        </>
      )}
    </Shell>
  );
};

export default WaterComparisonView;
